import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, BookOpen, Search, Sun, Moon, Bookmark, Settings, LogIn, LogOut, BarChart3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/lib/theme";
import { useBookmarks } from "@/lib/bookmarks";
import { useAuth } from "@/lib/auth";
import { SCHOLAR_ENABLED } from "@/lib/features";

function SoonBadge() {
  return (
    <span className="ms-1.5 align-middle text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[var(--color-gold)]/15 text-[var(--color-gold)]">
      Soon
    </span>
  );
}

/** Sign-in button, or the signed-in user's menu. Hidden in local mode (no Cognito). */
function AccountMenu() {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (!auth.enabled || auth.isLoading) return null;

  if (!auth.isAuthenticated) {
    return (
      <button
        onClick={auth.signIn}
        className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-[var(--color-midnight)] bg-[var(--color-gold)] hover:bg-[var(--color-gold)]/90 transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" /> Sign in
      </button>
    );
  }

  const initial = (auth.user?.name || auth.user?.email || "?")[0].toUpperCase();
  return (
    <div ref={ref} className="relative hidden md:block">
      <button
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="w-8 h-8 rounded-full bg-[var(--color-gold)]/20 text-[var(--color-gold)] text-sm font-bold hover:bg-[var(--color-gold)]/30 transition-colors"
      >
        {initial}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-xl border border-[var(--color-cream-dark)] py-2 text-sm">
          {auth.user?.email && <p className="px-4 py-2 text-xs text-[var(--color-ink-muted)] truncate">{auth.user.email}</p>}
          {auth.isAdmin && (
            <Link role="menuitem" to="/analytics" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-[var(--color-cream)]">
              <BarChart3 className="w-4 h-4" /> Analytics
            </Link>
          )}
          <button role="menuitem" onClick={auth.signOut} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-[var(--color-cream)] text-left">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const navLinks: { to: string; label: string; soon?: boolean }[] = [
  { to: "/seerah", label: "Seerah Explorer" },
  { to: "/timeline", label: "World Timeline" },
  { to: "/quran", label: "Quran Companion" },
  { to: "/clarifier", label: "Clarifier" },
  { to: "/isnad", label: "Isnad Explorer" },
  { to: "/dhikr", label: "Dhikr" },
  { to: "/learning", label: "Learning Paths" },
  { to: "/scholar", label: "Ask a Scholar", soon: !SCHOLAR_ENABLED },
];

export default function Navbar({ onSearchClick }: { onSearchClick?: () => void }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { toggleTheme, isDark } = useTheme();
  const { bookmarks } = useBookmarks();
  const auth = useAuth();

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHome
          ? "bg-transparent"
          : "bg-[var(--color-midnight)]/95 backdrop-blur-md border-b border-[var(--color-gold)]/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[var(--color-gold)] to-[var(--color-gold-dim)] flex items-center justify-center shadow-lg group-hover:shadow-[var(--color-gold)]/20 transition-shadow">
              <BookOpen className="w-5 h-5 text-[var(--color-midnight)]" />
            </div>
            <div className="hidden sm:block">
              <span className="text-[var(--color-cream)] font-[var(--font-display)] text-lg font-semibold tracking-tight">
                Deen Over Dunya
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname.startsWith(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "text-[var(--color-gold)] bg-[var(--color-gold)]/10"
                      : "text-[var(--color-cream)]/70 hover:text-[var(--color-cream)] hover:bg-white/5"
                  }`}
                >
                  {link.label}
                  {link.soon && <SoonBadge />}
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <button
              onClick={onSearchClick}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[var(--color-cream)]/50 text-sm hover:border-[var(--color-gold)]/30 hover:text-[var(--color-cream)]/70 transition-all"
            >
              <Search className="w-3.5 h-3.5" />
              <span>Search...</span>
              <kbd className="hidden lg:inline text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-[var(--color-cream)]/40 font-mono">⌘K</kbd>
            </button>

            {/* Bookmarks */}
            <Link
              to="/bookmarks"
              className="relative p-2 rounded-lg text-[var(--color-cream)]/50 hover:text-[var(--color-gold)] hover:bg-white/5 transition-all"
              title="Bookmarks"
              aria-label={`Bookmarks${bookmarks.length ? ` (${bookmarks.length})` : ""}`}
            >
              <Bookmark className="w-4 h-4" />
              {bookmarks.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[var(--color-gold)] text-[var(--color-midnight)] text-[9px] font-bold flex items-center justify-center">
                  {bookmarks.length}
                </span>
              )}
            </Link>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-[var(--color-cream)]/50 hover:text-[var(--color-gold)] hover:bg-white/5 transition-all"
              title={isDark ? "Light mode" : "Dark mode"}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Settings */}
            <Link
              to="/settings"
              className="hidden lg:flex p-2 rounded-lg text-[var(--color-cream)]/50 hover:text-[var(--color-gold)] hover:bg-white/5 transition-all"
              title="Settings"
              aria-label="Settings"
            >
              <Settings className="w-4 h-4" />
            </Link>

            <AccountMenu />

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg text-[var(--color-cream)]/70 hover:text-[var(--color-cream)] hover:bg-white/5 transition-all"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[var(--color-midnight)] border-t border-[var(--color-gold)]/10 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {/* Mobile search */}
              <button
                onClick={() => { setMobileOpen(false); onSearchClick?.(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-[var(--color-cream)]/50 hover:text-[var(--color-cream)] hover:bg-white/5 transition-all mb-2"
              >
                <Search className="w-4 h-4" />
                Search all content...
              </button>
              {navLinks.map((link) => {
                const isActive = location.pathname.startsWith(link.to);
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? "text-[var(--color-gold)] bg-[var(--color-gold)]/10"
                        : "text-[var(--color-cream)]/70 hover:text-[var(--color-cream)] hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                    {link.soon && <SoonBadge />}
                  {link.soon && <SoonBadge />}
                  </Link>
                );
              })}
              {/* Mobile bookmarks */}
              <Link
                to="/bookmarks"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-[var(--color-cream)]/70 hover:text-[var(--color-cream)] hover:bg-white/5 transition-all"
              >
                <Bookmark className="w-4 h-4" />
                Bookmarks {bookmarks.length > 0 && `(${bookmarks.length})`}
              </Link>
              <Link
                to="/settings"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-[var(--color-cream)]/70 hover:text-[var(--color-cream)] hover:bg-white/5 transition-all"
              >
                <Settings className="w-4 h-4" />
                Settings
              </Link>
              {auth.isAdmin && auth.enabled && (
                <Link
                  to="/analytics"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-[var(--color-cream)]/70 hover:text-[var(--color-cream)] hover:bg-white/5 transition-all"
                >
                  <BarChart3 className="w-4 h-4" />
                  Analytics
                </Link>
              )}
              {auth.enabled && (
                <button
                  onClick={() => { setMobileOpen(false); if (auth.isAuthenticated) auth.signOut(); else auth.signIn(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-[var(--color-gold)] hover:bg-white/5 transition-all"
                >
                  {auth.isAuthenticated ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                  {auth.isAuthenticated ? "Sign out" : "Sign in"}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
